import { createContext, useMemo } from 'react';
import { Outlet } from 'react-router-dom';
import type { JSX, ReactNode } from 'react';

import { getFragmentData } from '@e-socna/data-layer';

import { MissionAccountingPage_AccountingInfo_Fragment } from '../../pages/MissionAccounting/fragments/accounting-info.fragment';

import { useMissionContext } from '../MissionContext/hooks/useMissionContext.hook';
import { MissionContextStatus } from '../MissionContext/types/mission-context.type';

import { AccountingContextStatus } from './types/accounting-context.type';
import type { AccountingContextData } from './types/accounting-context.type';

const AccountingContext = createContext<AccountingContextData | null>(null);

interface Props {
  readonly children?: ReactNode;
}

const AccountingContextProvider = ({
  children,
}: Readonly<Props>): JSX.Element => {
  const { status: missionStatus, mission } = useMissionContext();

  const missionAccountingInfo = getFragmentData(
    MissionAccountingPage_AccountingInfo_Fragment,
    mission?.accountingInfo,
  );

  const contextValue = useMemo<AccountingContextData>(() => {
    if (missionStatus === MissionContextStatus.Loading) {
      return {
        status: AccountingContextStatus.Loading,
        missionAccountingInfo: undefined,
      };
    }

    if (missionStatus === MissionContextStatus.Error) {
      return {
        status: AccountingContextStatus.Error,
        missionAccountingInfo: undefined,
      };
    }

    if (!missionAccountingInfo) {
      return {
        status: AccountingContextStatus.NotInitialized,
        missionAccountingInfo: undefined,
      };
    }

    return {
      status: AccountingContextStatus.Ready,
      missionAccountingInfo,
    };
  }, [missionStatus, missionAccountingInfo]);

  return (
    <AccountingContext.Provider value={contextValue}>
      {children ?? <Outlet />}
    </AccountingContext.Provider>
  );
};

export { AccountingContext, AccountingContextProvider };
